import React, { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { styled } from '../../theme'
import { debounce } from '../../utils/debounce'

const ScrollableContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== '$height' && prop !== '$preventXOverflow',
})<{ $height?: number; $preventXOverflow: boolean }>`
  display: flex;
  flex-direction: column;
  height: ${(props) => (props.$height ? props.$height + 'px;' : '')};
  overflow: auto;
  ${(props) => (props.$preventXOverflow ? 'overflow-x: hidden;' : '')}
  -webkit-overflow-scrolling: touch;
`

interface Props {
  next: () => Promise<void>
  dataLength: number
  hasMore: boolean
  children: ReactNode
  loader: ReactNode
  height?: number
  preventXOverflow?: boolean
}

export function InfiniteScroll(this: any, props: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const scrollPosRef = useRef(0)

  const isElementAtBottom = (target: HTMLElement) => {
    return target.scrollTop + target.clientHeight >= (99 / 100) * target.scrollHeight
  }

  const handleScroll = async (event: Event) => {
    const target = event.target as HTMLElement
    const atBottom = isElementAtBottom(target)

    if (containerRef.current) {
      scrollPosRef.current = containerRef.current.scrollTop
    }

    if (atBottom && props.hasMore) {
      setIsLoading(true)
      await props.next()
      setIsLoading(false)
    }
  }

  const debouncedScroll = debounce(handleScroll, 150, this)

  useEffect(() => {
    const scrollable = document.getElementById('internal-scrollable')
    scrollable?.removeEventListener('scroll', debouncedScroll)
    scrollable?.addEventListener('scroll', debouncedScroll)
    return () => {
      scrollable?.removeEventListener('scroll', debouncedScroll)
    }
  }, [props])

  useLayoutEffect(() => {
    const el = containerRef.current
    if (el) {
      el.scrollTop = scrollPosRef.current
    }
  }, [props.children])

  return (
    <ScrollableContainer
      ref={containerRef}
      $height={props.height}
      $preventXOverflow={!!props.preventXOverflow}
      id="internal-scrollable"
    >
      {props.children}
      {isLoading && props.loader}
    </ScrollableContainer>
  )
}
